import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, Observable, of } from 'rxjs';
import { Municipality } from '@api/models/municipality.model';
import {
  DossierDetailPatch,
  DossierItemType,
  MunicipalityDossier,
} from '@api/models/dossier.model';
import { MunicipalityDocument } from '@api/models/municipality-document.model';
import { IndexResponse, ShowResponse } from '@api/models/response.model';
import { environment } from '@environments/environment';
import { AppService } from '../../app.service';

@Injectable({
  providedIn: 'root'
})
export class MunicipalitiesService {
  private http = inject(HttpClient);
  private appService = inject(AppService);
  private apiUrl = `${environment.apiUrl}/municipalities`;

  constructor() { }

  /**
   * Gecombineerde lijst aanvragen (request) en opdrachten (assignment).
   * @see municipalities.dossiers.index
   */
  getDossiers(
    page: number = 1,
    perPage: number = 50,
    filters: Record<string, string | boolean> = {},
    q: string = '',
    sort: string = 'created_at',
    direction: 'ASC' | 'DESC' = 'DESC',
    includeDetails = false
  ): Observable<IndexResponse<MunicipalityDossier>> {
    const municipalityId = this.appService.municipalityId();
    if (!municipalityId) {
      throw new Error('Geen gemeente geselecteerd');
    }

    const headers = new HttpHeaders().set('X-Municipality-Id', municipalityId);

    const filterParams = Object.entries(filters)
      .filter(([, value]) => value !== '' && value !== null && value !== undefined)
      .map(([key, value]) => `filter[${key}]=${encodeURIComponent(String(value))}`)
      .join('&');

    let url = `${this.apiUrl}/${municipalityId}/dossiers?page=${page}&per_page=${perPage}&sort=${encodeURIComponent(sort)}&direction=${direction}`;
    if (includeDetails) {
      url += `&include_details=true`;
    }
    if (q.trim()) {
      url += `&q=${encodeURIComponent(q.trim())}`;
    }
    if (filterParams) {
      url += `&${filterParams}`;
    }

    return this.http
      .get<IndexResponse<MunicipalityDossier>>(url, { headers })
      .pipe(map((r) => r));
  }

  /**
   * Batch: naam, adres, status voor zichtbare dossier-rijen (max 50 items per call).
   * @see municipalities.dossiers.details
   */
  postDossiersDetails(
    items: Array<{ item_type: DossierItemType; id: string }>
  ): Observable<{ data: DossierDetailPatch[] }> {
    const municipalityId = this.appService.municipalityId();
    if (!municipalityId) {
      throw new Error('Geen gemeente geselecteerd');
    }
    if (!items.length) {
      return of({ data: [] as DossierDetailPatch[] });
    }

    const headers = new HttpHeaders().set('X-Municipality-Id', municipalityId);

    return this.http.post<{ data: DossierDetailPatch[] }>(
      `${this.apiUrl}/${municipalityId}/dossiers/details`,
      { items },
      { headers }
    );
  }

  getMunicipalities(page: number = 1, perPage: number = 10, q?: string): Observable<IndexResponse<Municipality>> {
    let url = `${this.apiUrl}?page=${page}&per_page=${perPage}`;

    if (q && q.trim()) {
      url += `&q=${encodeURIComponent(q.trim())}`;
    }

    return this.http.get<IndexResponse<Municipality>>(url).pipe(
      map(response => response)
    );
  }

  getMunicipality(id: number): Observable<Municipality> {
    return this.http.get<ShowResponse<Municipality>>(`${this.apiUrl}/${id}`).pipe(
      map(response => response.data)
    );
  }

  createMunicipality(municipality: Municipality): Observable<Municipality> {
    return this.http.post<ShowResponse<Municipality>>(`${this.apiUrl}`, municipality).pipe(
      map(response => response.data)
    );
  }

  updateMunicipality(id: string, municipality: Municipality): Observable<Municipality> {
    return this.http.put<ShowResponse<Municipality>>(`${this.apiUrl}/${id}`, municipality).pipe(
      map(response => response.data)
    );
  }

  deleteMunicipality(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  private documentsHeaders(municipalityId: string): HttpHeaders {
    return new HttpHeaders().set('X-Municipality-Id', municipalityId);
  }

  /**
   * @see municipalities.documents.index
   */
  getDocuments(
    municipalityId: string,
    page: number = 1,
    perPage: number = 25
  ): Observable<IndexResponse<MunicipalityDocument>> {
    const headers = this.documentsHeaders(municipalityId);
    const url = `${this.apiUrl}/${municipalityId}/documents?page=${page}&per_page=${perPage}`;
    return this.http.get<IndexResponse<MunicipalityDocument>>(url, { headers });
  }

  /**
   * @see municipalities.documents.store
   */
  uploadDocument(
    municipalityId: string,
    file: File
  ): Observable<ShowResponse<MunicipalityDocument>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ShowResponse<MunicipalityDocument>>(
      `${this.apiUrl}/${municipalityId}/documents`,
      formData,
      { headers: this.documentsHeaders(municipalityId) }
    );
  }

  /**
   * @see municipalities.documents.download
   */
  downloadDocument(
    municipalityId: string,
    documentId: string
  ): Observable<Blob> {
    return this.http.get(
      `${this.apiUrl}/${municipalityId}/documents/${documentId}/download`,
      {
        headers: this.documentsHeaders(municipalityId),
        responseType: 'blob',
      }
    );
  }

  /**
   * @see municipalities.documents.destroy
   */
  deleteDocument(municipalityId: string, documentId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/${municipalityId}/documents/${documentId}`,
      { headers: this.documentsHeaders(municipalityId) }
    );
  }
}
